import { debounce } from '@cae/cobalt-react/utils';
import {
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from '@shared/utils/search/search.utils';
import * as React from 'react';

import type {
  UseSearchFieldOptions,
  UseSearchFieldReturn,
} from './useSearchField.types';

/**
 * Shared hook that encapsulates the debounced search-field logic.
 *
 * - Maintains a local (optimistic) value for immediate UI feedback.
 * - Debounces calls to `onSearch` so the API isn't hit on every keystroke.
 * - Fires `onSearch('')` immediately when the input drops below `minLength`
 *   or when the field is cleared, so results reset without waiting for the debounce.
 * - Syncs `localValue` whenever the external `value` changes (e.g. `clearFilters`).
 */
export function useSearchField({
  value,
  onSearch,
  minLength = MIN_SEARCH_LENGTH,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: UseSearchFieldOptions): UseSearchFieldReturn {
  const [localValue, setLocalValue] = React.useState(value);

  const committedValueRef = React.useRef(value);
  committedValueRef.current = value;

  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;

  const debouncedOnSearch = React.useMemo(
    () => debounce((val: string) => onSearchRef.current(val), debounceMs),
    [debounceMs]
  );

  React.useEffect(() => {
    return () => debouncedOnSearch.cancel();
  }, [debouncedOnSearch]);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const newValue = event.target.value;
      setLocalValue(newValue);

      const effectiveLength = newValue.replaceAll(/\s/g, '').length;

      if (effectiveLength >= minLength) {
        debouncedOnSearch(newValue);
      } else {
        debouncedOnSearch.cancel();
        if (committedValueRef.current) {
          onSearchRef.current('');
        }
      }
    },
    [debouncedOnSearch, minLength]
  );

  const handleClear = React.useCallback((): void => {
    setLocalValue('');
    debouncedOnSearch.cancel();
    onSearchRef.current('');
  }, [debouncedOnSearch]);

  return { localValue, handleChange, handleClear };
}
